import { useAeropayUser } from '@iheartjane/data-access/react-query';
import { AppMode } from '@iheartjane/dm-sdk';
import { getFlagContext } from '@iheartjane/feature-flags';
import { useWindowListener } from '@iheartjane/reefer';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { getJwt } from '@jane/shared/auth';
import { LoadingWrapper } from '@jane/shared/components';
import { useGetStore, useJaneUser } from '@jane/shared/data-access';
import type { Store } from '@jane/shared/types';
import { statePostalCode } from '@jane/shared/util';

import {
  ConfirmationScreen,
  CreateAccountScreen,
  ForgotPasswordScreen,
  SignInScreen,
  SignInToggleButtons,
  SuccessScreen,
} from '../../components';
import type { RootLoaderType } from '../../data-access';
import {
  SCREEN_NAMES,
  useKioskAuth,
  useShouldKioskShowGold,
  useTypedRouteLoaderData,
} from '../../hooks';
import { useSessionContext } from '../../providers';
import type { SsoChannel } from '../../util';
import {
  getUsername,
  trackAbandonedSignIn,
  trackBeginsSignIn,
  trackLinkBankAccountTap,
  trackRegistered,
  trackSignInTimeout,
  trackSignedIn,
} from '../../util';

const getStep = (screen: string) => {
  if (screen === 'create-account') return 'Information';
  return 'Sign in';
};

export const LoginOnDevice = () => {
  const hasSentTrackingEvent = useRef(false);
  const hasSentTimeoutTrackingEvent = useRef(false);
  const hasSignedIn = useRef(false);

  const { store: initialStore, storeId } =
    useTypedRouteLoaderData<RootLoaderType>('root');
  const { data: store } = useGetStore<Store>(storeId, {
    initialData: initialStore,
  });
  const [queryParams] = useSearchParams();
  const userCode = queryParams.get('user_code');
  const source = queryParams.get('source') || '';
  const navigate = useNavigate();
  const showGold = useShouldKioskShowGold();
  const { flagClient, jdid } = useSessionContext();

  const { data: janeUser, isFetching: isUserFetching } = useJaneUser();
  const { data: aeropayData, isFetching: isFetchingAeropayData } =
    useAeropayUser({
      janeUserId: janeUser?.user?.id,
      jwt: getJwt(),
    });
  const userName = useMemo(
    () => (janeUser ? getUsername(janeUser.user) : ''),
    [janeUser]
  );
  const userHasLinkedBank = !!aeropayData?.bank_accounts.length;
  const needsMoreInfoForLinkBank =
    !janeUser?.user?.first_name ||
    !janeUser?.user?.last_name ||
    !janeUser?.user?.phone;

  const onLoginSuccess = (ssoChannel: SsoChannel) => {
    hasSignedIn.current = true;
    trackSignedIn({
      source: [source],
      ssoChannel,
      store,
    });
  };

  const onRegisterSuccess = (ssoChannel: SsoChannel) => {
    hasSignedIn.current = true;
    trackRegistered({
      source: [source],
      ssoChannel,
      store,
    });
  };

  const {
    loginError,
    registerErrors,
    resetPasswordError,
    forgotPasswordScreen,
    screen,
    setForgotPasswordScreen,
    handleSetScreen,
    onAppleLogin,
    onGoogleLogin,
    signInSubmit,
    createAccountSubmit,
    resetPasswordSubmit,
    logoutUser,
    onAuthSuccess: kioskDeviceAuthorization,
  } = useKioskAuth({
    onLoginSuccess,
    onRegisterSuccess,
    storeId,
    userCode: userCode ?? '',
  });

  useEffect(() => {
    if (store && source && !hasSentTrackingEvent.current) {
      trackBeginsSignIn({
        store,
        source: [source],
      });
      hasSentTrackingEvent.current = true;
    }
  }, [store, source]);

  /** @TODO include "Verification" in abandoned tracking (figure out how to know we sent an mfa but it wasn't completed) */
  const handleBeforeUnload = useCallback(() => {
    if (!hasSignedIn.current) {
      trackAbandonedSignIn({
        step: getStep(screen ?? ''),
        store,
        source: [source],
      });
    }
  }, [screen, source, store]);

  useEffect(() => {
    if (
      loginError.includes('Your link has expired') &&
      !hasSentTimeoutTrackingEvent.current
    ) {
      hasSentTimeoutTrackingEvent.current = true;
      trackSignInTimeout({
        store,
        source: [source],
      });
    }
  }, [loginError, source, store]);

  const handleOnLinkBank = () => {
    const step = needsMoreInfoForLinkBank
      ? SCREEN_NAMES.moreInfo
      : SCREEN_NAMES.aeropay;

    trackLinkBankAccountTap({
      deviceType: 'Mobile',
      source: [source],
      store,
    });
    navigate(
      `/stores/${storeId}/mobile/link-bank?step=${step}&source=${source}`,
      { state: { fromMobile: true } }
    );
  };

  useWindowListener('beforeunload', handleBeforeUnload);

  useEffect(() => {
    if (!isUserFetching && !screen) {
      handleSetScreen(
        janeUser?.user.authenticated ? 'confirmation' : 'sign-in'
      );
    }
  }, [isUserFetching, screen, handleSetScreen, janeUser]);

  // identify launch darkly user context on jdid or user change for proper flag targeting
  useEffect(() => {
    async function identifyContext() {
      await flagClient?.identify(
        getFlagContext({
          extraAttributes: {
            appMode: AppMode.Kiosk,
            state: statePostalCode(store?.state),
            storeId: store?.id,
            organizationId: store?.organization_id ?? undefined,
          },
          janeDeviceId: jdid,
          user: { ...janeUser?.user },
        })
      );
    }
    identifyContext();
  }, [flagClient, store, jdid, janeUser]);

  return (
    <LoadingWrapper isLoading={!screen} variant="full-page">
      {(screen === 'sign-in' || screen === 'create-account') && (
        <SignInToggleButtons screen={screen} setScreen={handleSetScreen} />
      )}

      {screen === 'sign-in' && (
        <SignInScreen
          storeName={store.name}
          onSubmit={signInSubmit}
          onForgotPassword={() => {
            setForgotPasswordScreen('form');
            handleSetScreen('forgot-password');
          }}
          onAppleClick={onAppleLogin}
          onGoogleClick={onGoogleLogin}
          error={loginError}
        />
      )}

      {screen === 'create-account' && (
        <CreateAccountScreen
          onSubmit={createAccountSubmit}
          onAppleClick={onAppleLogin}
          onGoogleClick={onGoogleLogin}
          errors={registerErrors}
        />
      )}

      {screen === 'forgot-password' && (
        <ForgotPasswordScreen
          error={resetPasswordError}
          forgotPasswordScreen={forgotPasswordScreen}
          onBack={() => handleSetScreen('sign-in')}
          onSubmit={resetPasswordSubmit}
        />
      )}

      {screen?.includes('success') && (
        <SuccessScreen
          storeName={store.name}
          userName={userName ?? ''}
          showJaneGold={showGold}
          loadingBanks={isFetchingAeropayData}
          hasLinkedBank={userHasLinkedBank}
          newUser={screen === 'new-success'}
          showJanePay={Boolean(store?.jane_pay_integration?.enabled)}
          hideBranding={Boolean(store?.jane_pay_integration?.white_label)}
          onLinkBank={handleOnLinkBank}
        />
      )}

      {screen === 'confirmation' && (
        <ConfirmationScreen
          userName={userName ?? ''}
          storeName={store.name}
          loginOnKiosk={kioskDeviceAuthorization}
          logoutUser={logoutUser}
          error={loginError}
        />
      )}
    </LoadingWrapper>
  );
};
